import React from 'react';
import classnames from 'classnames';
import Label from 'components/form/Label/Label';
import '../style/GradeDialView.scss';
import { Image } from 'components/Image/Image';
import { FlexContainer } from 'components/Containers/FlexContainer/FlexContainer';
import { CSTooltip } from 'components/Tooltip/CSToolTip';
import { ConditionalRendering } from 'helpers/conditionalRedering.helper';
import CreditFactorChart from './CreditFactorChart/CreditFactorChart';
import CreditScoreDial from './ScoreDial/ScoreDial';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { getViewModuleTrackingAttributes } from 'helpers/tracking.helpers';
import { CREDIT_SCORE_DIAL } from 'types/morph.pagePosition.constants';
import { useSeenModulePixel, useKeyPressWithRef } from 'hook/helper.hook';
import { useConfig } from 'hook/config.hook';
import { Link } from 'components/navigation/Link/Link';
import { getImprovementScore, getTUCreditScore } from '../helpers/utils';
import GoToFullCreditReportModule from 'partial/OfferManager/modules/GoToFullCreditReportModule';
import { Button } from 'components/form/Button/Button';
import { ReactComponent as ChevronRight } from 'style/_assets/icons/chevron-right.svg';
import { LoadingOutlined } from '@ant-design/icons';
import { Spin } from 'antd';

const LoadingPotentialScore = () => {
  return (
    <Spin
      indicator={
        <LoadingOutlined
          style={{
            fontSize: 14,
            color: 'black',
            top: '0px',
          }}
          spin
        />
      }
    />
  );
};

const PotentialScore = props => {
  const {
    loading,
    potentialScoreData,
    score,
    scoreImprovementData,
    isPotentialView,
    config,
  } = props;
  if (loading) {
    return <LoadingPotentialScore />;
  } else {
    if (
      (potentialScoreData?.[0]?.score && scoreImprovementData !== 0) ||
      (isPotentialView && config?.showPotentialScoreData)
    ) {
      return (
        <FlexContainer row justifyCenter alignItemsCenter className={'padL7'}>
          <Label tabIndex={'-1'}>{score}</Label>{' '}
          <ChevronRight aria-label="chevron-right-icon" />
        </FlexContainer>
      );
    } else {
      return (
        <Label
          type={'Subtitle-3'}
          className={'system_neutral_n500_text bold'}
          tabIndex="-1"
        >
          View
        </Label>
      );
    }
  }
};

const PotentialToggle = props => {
  const {
    creditScore,
    isPotentialView,
    loading,
    potentialScoreData,
    isSomeFactorClicked,
    handleClickPotential,
    todayContainerLoading,
    scoreImprovementData,
  } = props;
  const { config } = useConfig();

  const score = isPotentialView
    ? getTUCreditScore(creditScore)
    : getTUCreditScore(potentialScoreData);

  const loadingScore = loading || todayContainerLoading;

  const toggleLabel = isPotentialView ? 'Current' : 'Potential';

  const buttonClass = classnames('btnPotentialScore', {
    'disabled-button':
      loadingScore &&
      !isPotentialView &&
      (potentialScoreData?.[0]?.score === undefined ||
        scoreImprovementData === 0),
  });

  return (
    <FlexContainer className={'justify-between items-align-end'}>
      <FlexContainer>
        {isPotentialView && !config?.removePotentialIconTooltip && (
          <CSTooltip
            className={classnames('position-relative', {
              showingInfo: isSomeFactorClicked,
            })}
            overlayStyle={{ maxWidth: '259px' }}
            placement="topLeft"
            title={config?.simulations?.tooltipDisclaimerText?.title_inHouse}
            tooltipContent={config?.simulations?.tooltipDisclaimerText?.inHouse}
          >
            <ExclamationCircleOutlined />
          </CSTooltip>
        )}
      </FlexContainer>
      <FlexContainer
        className={
          'margB0 align-items-center flex-column left20 position-relative width62px'
        }
      >
        <Label type={'Subtitle-3 bold'}>{toggleLabel}</Label>
        <Button
          mrphType="primary"
          disable={loadingScore}
          onClick={handleClickPotential}
          className={buttonClass}
          fullWidth
          rounded
        >
          <PotentialScore
            loading={loadingScore}
            potentialScoreData={potentialScoreData}
            score={score}
            scoreImprovementData={scoreImprovementData}
            isPotentialView={isPotentialView}
            config={config}
          />
        </Button>
      </FlexContainer>
    </FlexContainer>
  );
};

const GradeDialView = props => {
  const {
    donutRef,
    showDonut,
    isFactorContainerLoading,
    currentBureau,
    selectedFactor,
    scoreData,
    factorData,
    potentialScoreData,
    potentialFactorData,
    potentialCreditProfileContainer,
    isSomeBureauClicked,
    isSomeFactorClicked,
    handleClickBureauDial,
    handleClickDonut,
    handleGoToCreditReports,
    closeFocusedFactor,
    isPotentialView,
    userInfo,
    isPremiumReactivatePendingCancellationUser,
    isPremiumReactivateCancelUser,
    isPlusLaunchWebFlagEnabled,
    ableToPurchase3BReport,
    handleClickPotential,
    potentialCreditProfileContainerLoading,
    potentialCreditProfileContainerError,
    handleTryAgainPotentialCreditProfile,
    showEngagementModuleFromFlag,
    config,
    subscriptionLandingUrl,
    todayContainerLoading,
    subscriptionInfo,
  } = props;

  const closeIconRef = React.useRef(null);
  useKeyPressWithRef(closeIconRef, closeFocusedFactor);

  const trackingAtr = getViewModuleTrackingAttributes({
    pagePosition: CREDIT_SCORE_DIAL + ' ' + currentBureau,
    modulePosition: props.modulePosition,
  });
  const seenModuleTracking = useSeenModulePixel(trackingAtr);

  const scoreImprovementData = getImprovementScore(
    potentialCreditProfileContainer,
  );

  let FactorDialView = (
    <FlexContainer
      className={classnames('factorContainer', 'flex-column', 'justify-end', {
        showingInfo: isSomeFactorClicked,
        highlighted: isSomeBureauClicked,
        potentialView: isPotentialView,
      })}
    >
      {isSomeFactorClicked && (
        <div
          tabIndex="0"
          role="button"
          ref={closeIconRef}
          className="crossIconDonut"
          onClick={closeFocusedFactor}
          aria-label="cross Icon donut"
        >
          <Image className="CrossImage" />
        </div>
      )}

      <ConditionalRendering isTrue={showDonut}>
        <CreditFactorChart
          className={classnames('factorDonut')}
          data={factorData.data || []}
          csGrade={factorData?.sesameGrade}
          donutRef={donutRef}
          isSomeFactorClicked={isSomeFactorClicked}
          selectedFactor={selectedFactor}
          isPotentialView={isPotentialView}
          handleClickPotential={handleClickPotential}
          handleGoToCreditReports={handleGoToCreditReports}
          showEngagement={props.showEngagement}
          setShowEngagement={props.setShowEngagement}
          eventEngagementData={props.eventEngagementData}
          clickDonut={handleClickDonut}
          history={props.history}
          showEngagementModuleFromFlag={showEngagementModuleFromFlag}
          config={config}
        />
      </ConditionalRendering>

      {!isSomeFactorClicked && isSomeBureauClicked && (
        <PotentialToggle
          potentialScoreData={potentialScoreData}
          loading={potentialCreditProfileContainerLoading}
          isPotentialView={isPotentialView}
          isSomeFactorClicked={isSomeFactorClicked}
          handleClickPotential={handleClickPotential}
          todayContainerLoading={todayContainerLoading}
          scoreImprovementData={scoreImprovementData}
        />
      )}
    </FlexContainer>
  );

  if (isPotentialView) {
    FactorDialView = (
      <div className="emptyFactorDialContainer">
        <Image className="emptyFactorDial" />
      </div>
    );
  }

  let checkingMessage = null;
  if (isPotentialView && !showDonut) {
    checkingMessage = (
      <FlexContainer className="flex-column items-align-center primary_text_light bold text-16 m-margT16">
        Checking your potential credit score…
      </FlexContainer>
    );
  }

  if (isPotentialView) {
    FactorDialView = showDonut ? (
      <FlexContainer
        className={classnames('factorContainer', 'flex-column', 'justify-end', {
          showingInfo: isSomeFactorClicked,
          highlighted: isSomeBureauClicked,
          potentialView: isPotentialView,
        })}
      >
        <CreditFactorChart
          className={classnames('factorDonut')}
          data={potentialFactorData?.data || []}
          csGrade={potentialFactorData?.sesameGrade}
          donutRef={donutRef}
          isSomeFactorClicked={isSomeFactorClicked}
          selectedFactor={selectedFactor}
          isPotentialView={isPotentialView}
          clickDonut={handleClickDonut}
          potentialCreditProfileContainerError={
            potentialCreditProfileContainerError
          }
          handleTryAgainPotentialCreditProfile={
            handleTryAgainPotentialCreditProfile
          }
          config={config}
        />

        {!isSomeFactorClicked && (
          <PotentialToggle
            isPotentialView={isPotentialView}
            creditScore={scoreData}
            potentialScoreData={potentialScoreData}
            isSomeFactorClicked={isSomeFactorClicked}
            handleClickPotential={handleClickPotential}
          />
        )}
      </FlexContainer>
    ) : (
      <div className="emptyFactorDialContainer">
        <Image className="emptyFactorDial" />
      </div>
    );
  }

  return (
    <FlexContainer
      column
      className={classnames('GradeDialView format100', {
        showingInfo: isSomeFactorClicked,
      })}
    >
      {checkingMessage}
      {seenModuleTracking}
      {/* 3B or 1B Credit score dials  */}
      {isPotentialView ? (
        <CreditScoreDial
          creditScore={potentialScoreData}
          isPotentialView={isPotentialView}
          userInfo={userInfo}
          config={config}
          eventHandler={props?.eventHandler}
          isPremiumReactivatePendingCancellationUser={
            isPremiumReactivatePendingCancellationUser
          }
          isPremiumReactivateCancelUser={isPremiumReactivateCancelUser}
          isPlusLaunchWebFlagEnabled={isPlusLaunchWebFlagEnabled}
          ableToPurchase3BReport={ableToPurchase3BReport}
          isFactorContainerLoading={isFactorContainerLoading}
          todayContainerLoading={todayContainerLoading}
          showDonut={showDonut}
          potentialCreditProfileContainerError={
            potentialCreditProfileContainerError
          }
          subscriptionLandingUrl={subscriptionLandingUrl}
          subscriptionInfo={subscriptionInfo}
        />
      ) : (
        <CreditScoreDial
          creditScore={scoreData}
          currentBureau={currentBureau}
          isPotentialView={isPotentialView}
          isSomeFactorClicked={isSomeFactorClicked}
          isSomeBureauClicked={isSomeBureauClicked}
          handleClick={handleClickBureauDial}
          userInfo={userInfo}
          config={config}
          eventHandler={props?.eventHandler}
          isPremiumReactivatePendingCancellationUser={
            isPremiumReactivatePendingCancellationUser
          }
          isPremiumReactivateCancelUser={isPremiumReactivateCancelUser}
          isPlusLaunchWebFlagEnabled={isPlusLaunchWebFlagEnabled}
          ableToPurchase3BReport={ableToPurchase3BReport}
          isFactorContainerLoading={isFactorContainerLoading}
          todayContainerLoading={todayContainerLoading}
          potentialCreditProfileContainerError={
            potentialCreditProfileContainerError
          }
          subscriptionLandingUrl={subscriptionLandingUrl}
          subscriptionInfo={subscriptionInfo}
        />
      )}
      {/* Credit factor donut chart */}

      {FactorDialView}

      {isSomeFactorClicked && (
        <GoToFullCreditReportModule className={'m-hide margT22 gap-16'} />
      )}
      {!isSomeFactorClicked && (
        <FlexContainer
          className={'justify-center margT44 m-margT16'}
          tab-index={'-1'}
        >
          <Link
            to={config?.goalsLinkUnderCreditFactorRing?.url}
            className={'align-self-center pointer'}
            ariaLabel={`goals-link`}
            tabIndexLink={
              config?.goalsLinkUnderCreditFactorRing?.linkText ? '0' : '-1'
            }
          >
            <Label
              color="fontBlue100"
              className={'text-20 m-text-18 margt10 align-self-center pointer'}
              tabIndex="-1"
            >
              {config?.goalsLinkUnderCreditFactorRing?.linkText}
            </Label>
          </Link>
        </FlexContainer>
      )}
    </FlexContainer>
  );
};

export default GradeDialView;
